/**
 * 計測。
 *
 * 見たいのは 3 つだけ — **継続率・セッション長・離脱地点。**
 * ロードマップの通過条件（D1 30% 以上、1 セッション 5 分以上）を判定するための
 * 最小限で、それ以外は集めない。
 *
 * 守っていること:
 *
 * - **計測がゲームを壊さない。** 送信の失敗は全部握り潰す。
 *   サーバーが無ければ端末内に溜めるだけで、遊びは何も変わらない。
 * - **個人を特定できるものを送らない。** player_id はサーバーが振った乱数で、
 *   氏名にもメールにも紐づいていない。IP・UA・国は送らないし保存もしない。
 * - **離脱をちゃんと拾う。** いちばん知りたい `run_abandoned` は
 *   タブを閉じる瞬間に出るので、`sendBeacon` で送る。
 *   `fetch` だと画面が消えた時点で捨てられ、**知りたいものだけが取れない。**
 * - **閉じる瞬間の 1 回に賭けない。** その `sendBeacon` が通るかはブラウザ次第で、
 *   握り潰す環境がある。しかも戻り値は「渡した」であって「届いた」ではないので、
 *   **受領を確認できるまで端末に残す。** 次に開いたときに送り直し、
 *   二重になったぶんはサーバーが落とす（届かないより、2 度届く方がまし）。
 */

import { apiBase, currentIdentity } from './identity';

export interface IAnalytics {
  track(event: string, props?: Record<string, number | string | boolean>): void;
}

/**
 * 端末内にだけ溜める計測。
 *
 * サーバーが無いときの実装であり、`RemoteAnalytics` の書き込み先でもある。
 * 送信できているかに関わらず `LocalAnalytics.dump()` で手元確認ができる。
 */
const ANALYTICS_KEY = 'etw:analytics';
const ANALYTICS_CAP = 400;

export interface AnalyticsEvent {
  readonly event: string;
  readonly at: number;
  readonly props?: Record<string, number | string | boolean>;
}

export class LocalAnalytics implements IAnalytics {
  track(event: string, props?: Record<string, number | string | boolean>): void {
    try {
      const raw = localStorage.getItem(ANALYTICS_KEY);
      const list = raw ? (JSON.parse(raw) as AnalyticsEvent[]) : [];
      list.push({ event, at: Date.now(), props });
      // 溜め続けると容量を食うので、新しいものから一定数だけ残す。
      localStorage.setItem(ANALYTICS_KEY, JSON.stringify(list.slice(-ANALYTICS_CAP)));
    } catch {
      // 計測が保存できなくても遊べる方を優先する。
    }
    if (import.meta.env?.DEV) console.info('[analytics]', event, props ?? {});
  }

  /** 検証用に取り出す。被験者テストのあと、端末から読み出して集計する。 */
  static dump(): AnalyticsEvent[] {
    try {
      const raw = localStorage.getItem(ANALYTICS_KEY);
      return raw ? (JSON.parse(raw) as AnalyticsEvent[]) : [];
    } catch {
      return [];
    }
  }
}

/**
 * ページが閉じられる直前に呼ぶもの。
 *
 * **順番を偶然に任せない。** 離脱イベントを `pagehide` に自前で張ると、
 * 計測側の送信リスナーとどちらが先に走るかが登録順で決まってしまい、
 * 送信が先に走れば**いちばん知りたいイベントだけが毎回落ちる**。
 * ここに登録したものは、必ず送信より前に走る。
 */
const beforeFlush = new Set<() => void>();

export function onBeforeFlush(callback: () => void): () => void {
  beforeFlush.add(callback);
  return () => beforeFlush.delete(callback);
}

/** ページが隠れたときに走らせる。同じ「隠れ」で二重に走らせない。 */
let closing: (() => void) | null = null;
let hidden = false;

function endOfPage(): void {
  if (hidden) return;
  hidden = true;
  for (const callback of beforeFlush) {
    try {
      callback();
    } catch {
      // 1 つ転んでも残りは走らせる。
    }
  }
  closing?.();
}

// **戻ってきたら、もう一度張り直す。** 以前はここを一度きりにしていて、
// 「別のタブへ移って、戻って、遊び続けて、最後に閉じる」という普通の遊び方だと
// 最初に離れた時点で計測が終わっていた。セッション長がそこで切られ、
// 最後に閉じたことは何も残らない。
function backOnPage(): void {
  hidden = false;
}

// **`visibilitychange` だけでは足りない。** タブを閉じるときやアプリを
// スワイプで消すときは `pagehide` しか来ないことがあり、実測でも
// `visibilitychange` は来なかった。両方張って、同じ隠れでは 1 度だけにする。
if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') endOfPage();
    else backOnPage();
  });
  window.addEventListener('pagehide', () => endOfPage());
  window.addEventListener('pageshow', () => backOnPage());
}

/** これ以上溜まったら送る。 */
const FLUSH_AT = 12;
/** 溜まっていなくてもこの間隔で送る（ミリ秒）。 */
const FLUSH_EVERY_MS = 20_000;
/** 1 回の送信で送る上限。サーバー側の上限と合わせる。 */
const MAX_BATCH = 50;

/**
 * まだ届いたと確認できていないぶんの置き場。
 *
 * **閉じる瞬間の 1 回に賭けない。** 閉じるときの送信（`sendBeacon`）が
 * 通るかはブラウザ次第で、握り潰す環境がある。
 * ここに残しておけば、**次にゲームを開いたときに出ていく。**
 *
 * **`sendBeacon` の戻り値を「届いた」と読んではいけない。** あれは
 * 「ブラウザに渡した」でしかない。実際、`true` が返るのに 1 件も
 * 届かない環境があり、戻り値を信じてここから消していたせいで、
 * 離脱イベントが**毎回まるごと消えていた**。
 * 消すのは、応答が返る送り方（`fetch`）で受領を確認できたときだけにする。
 * その結果 2 度送ることがあるが、重複はサーバー側が落とす。
 */
const PENDING_KEY = 'etw:analytics:pending';
const PENDING_CAP = 200;
/** 続けてこれだけ失敗したら、この読み込みでは諦める（控えは残す）。 */
const GIVE_UP_AFTER = 3;
/** これより古いものは捨てる。サーバーが時刻を今に書き換えるので重複判定が壊れる。 */
const PENDING_MAX_AGE_MS = 20 * 60 * 60 * 1000;

type Props = Record<string, number | string | boolean>;

function loadPending(): AnalyticsEvent[] {
  try {
    const raw = localStorage.getItem(PENDING_KEY);
    const list = raw ? (JSON.parse(raw) as AnalyticsEvent[]) : [];
    if (!Array.isArray(list)) return [];
    const oldest = Date.now() - PENDING_MAX_AGE_MS;
    return list.filter((e) => typeof e?.at === 'number' && e.at >= oldest);
  } catch {
    return [];
  }
}

/** 同じイベントを指す鍵。サーバー側の重複判定と同じ組み合わせにする。 */
function keyOf(e: AnalyticsEvent): string {
  return `${e.event}|${e.at}`;
}

function savePending(list: AnalyticsEvent[]): void {
  try {
    if (list.length === 0) localStorage.removeItem(PENDING_KEY);
    else localStorage.setItem(PENDING_KEY, JSON.stringify(list.slice(-PENDING_CAP)));
  } catch {
    // 保存できなくても遊べる方を優先する。
  }
}

/**
 * サーバーへ送る計測。端末内にも書き続けるので、
 * `LocalAnalytics.dump()` での手元確認はそのまま使える。
 */
export class RemoteAnalytics implements IAnalytics {
  private readonly base: string;
  private readonly local = new LocalAnalytics();
  private queue: AnalyticsEvent[] = [];
  private timer: ReturnType<typeof setInterval> | null = null;
  private sending = false;
  private failures = 0;
  private readonly startedAt = Date.now();
  /** この読み込みの通し番号。同じセッションの報告をサーバー側でまとめるのに使う。 */
  private readonly session = `${Date.now().toString(36)}${Math.random().toString(36).slice(2, 8)}`;

  constructor(base: string) {
    this.base = base;
    // 前回送れなかったぶんを先に引き取る。
    this.queue = loadPending();
    this.timer = setInterval(() => this.flush(false), FLUSH_EVERY_MS);
    // ページの終わりの処理はモジュール側が握っている。
    // onBeforeFlush に登録されたものが全部走ったあとで、ここが呼ばれる。
    closing = () => this.closeSession();
    if (this.queue.length > 0) this.flush(false);
  }

  track(event: string, props?: Props): void {
    this.local.track(event, props);
    this.queue.push({ event, at: Date.now(), props });
    savePending(this.queue);
    if (this.queue.length >= FLUSH_AT) this.flush(false);
  }

  /**
   * 画面が隠れた時点までの滞在秒数を送る。
   *
   * **隠れるたびに送り直す。** 戻ってきて遊び続けることがあるので、
   * 最初に隠れたときの秒数で打ち切ると、セッション長が実際より短く出る。
   * 同じ `session` を付けて何度も送り、**サーバー側で一番長いものだけを採る。**
   */
  private closeSession(): void {
    const seconds = Math.round((Date.now() - this.startedAt) / 1000);
    // 一瞬開いて閉じただけのものはセッションとして数えない。
    if (seconds >= 3) {
      const props = { seconds, session: this.session };
      this.local.track('session_end', props);
      this.queue.push({ event: 'session_end', at: Date.now(), props });
      savePending(this.queue);
    }
    this.flush(true);
  }

  /** 受領を確認できたぶんだけ控えから消す。 */
  private confirm(events: AnalyticsEvent[]): void {
    const sent = new Set(events.map(keyOf));
    this.queue = this.queue.filter((e) => !sent.has(keyOf(e)));
    savePending(this.queue);
  }

  /** `beacon` のときは応答を待たない送り方にする。 */
  private flush(beacon: boolean): void {
    if (this.queue.length === 0) return;
    const events = this.queue.slice(0, MAX_BATCH);
    const payload = JSON.stringify({ playerId: currentIdentity().playerId, events });
    const url = `${this.base}/events`;

    try {
      if (beacon && navigator.sendBeacon) {
        // text/plain にするとプリフライトが要らない。閉じる瞬間に往復する余裕は無い。
        // **届いたかは分からないので、控えは残したまま。**
        navigator.sendBeacon(url, new Blob([payload], { type: 'text/plain' }));
        return;
      }
      // 送信の往復中に次の送信を始めない。同じものを 2 度出すだけになる。
      if (this.sending) return;
      // **遮断されている端末で永久に叩き続けない。** 拡張機能が `/events` を
      // 落とす環境があり（`ERR_BLOCKED_BY_CLIENT`）、そこでは何度出しても通らない。
      // 控えは残したままにするので、遮断が外れれば次の起動で出ていく。
      if (this.failures >= GIVE_UP_AFTER) return;
      this.sending = true;
      void fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'text/plain' },
        body: payload,
        keepalive: true,
      })
        // **4xx も消す。** こちらの出し方が悪いので送り直しても同じで、
        // 残すと二度と減らない。落ちているだけ（5xx・通信断）のときは残す。
        .then((res) => {
          if (res.status < 500) {
            this.failures = 0;
            this.confirm(events);
          } else {
            this.failures += 1;
          }
        })
        .catch(() => {
          this.failures += 1;
        })
        .finally(() => {
          this.sending = false;
        });
    } catch {
      this.sending = false;
      // 計測が送れなくても遊べる方を優先する。
    }
  }

  /** テストと後片付け用。 */
  dispose(): void {
    if (this.timer !== null) clearInterval(this.timer);
    this.timer = null;
  }
}

/** サーバーがあれば送る、無ければ端末内だけ。ゲーム側はどちらか知らない。 */
export function createAnalytics(): IAnalytics {
  const base = apiBase();
  return base ? new RemoteAnalytics(base) : new LocalAnalytics();
}
